import { z } from 'zod';
import { id, paginationQuery, paginatedResult, timestamp } from './common.js';
import { supportCaseCategory, supportCaseDetail, supportCaseStatus } from './support-case.js';

export const supportPriority = z.enum(['low', 'normal', 'high', 'urgent']);
export const supportAssignment = z.enum(['all', 'mine', 'unassigned']);
export const supportOperationsQuery = paginationQuery.extend({
  search: z.string().trim().max(100).optional(),
  status: supportCaseStatus.optional(),
  category: supportCaseCategory.optional(),
  priority: supportPriority.optional(),
  assignment: supportAssignment.default('all'),
});
export type SupportOperationsQuery = z.infer<typeof supportOperationsQuery>;
export const supportOperationsState = z.object({
  priority: supportPriority,
  assignedUserId: id.nullable(),
  assignedName: z.string().nullable(),
  operationsRevision: z.number().int().positive(),
  operationsUpdatedAt: timestamp.nullable(),
});
export type SupportOperationsState = z.infer<typeof supportOperationsState>;
export const supportQueueItem = supportOperationsState.extend({
  id,
  reference: z.string(),
  subject: z.string(),
  category: supportCaseCategory,
  status: supportCaseStatus,
  revision: z.number().int().positive(),
  bookingId: id,
  locationName: z.string(),
  professionalName: z.string(),
  createdAt: timestamp,
  updatedAt: timestamp,
});
export const supportQueuePage = paginatedResult(supportQueueItem);
export const supportOperationKind = z.enum(['claim', 'unassign', 'priority', 'internal_note']);
export const supportOperation = z.object({
  id,
  caseId: id,
  kind: supportOperationKind,
  actorUserId: id,
  actorName: z.string(),
  body: z.string(),
  revision: z.number().int().positive(),
  recordedAt: timestamp,
});
export type SupportOperation = z.infer<typeof supportOperation>;
export const supportOperatorDetail = supportCaseDetail.extend({
  operations: supportOperationsState,
  internalHistory: paginatedResult(supportOperation),
});
export type SupportOperatorDetail = z.infer<typeof supportOperatorDetail>;
export const supportOperatorDetailQuery = paginationQuery.extend({
  internalPage: z.coerce.number().int().positive().default(1),
});
export type SupportOperatorDetailQuery = z.infer<typeof supportOperatorDetailQuery>;
export const supportOperationInput = z
  .object({
    action: supportOperationKind,
    priority: supportPriority.optional(),
    body: z.string().trim().max(5000).default(''),
    operationsRevision: z.number().int().positive(),
    clientRequestId: z.uuid(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.action === 'priority' && !value.priority)
      ctx.addIssue({ code: 'custom', path: ['priority'], message: 'Choose a priority.' });
    if (value.action !== 'priority' && value.priority)
      ctx.addIssue({
        code: 'custom',
        path: ['priority'],
        message: 'Priority only applies to a priority change.',
      });
    if (value.action === 'internal_note' && value.body.length < 3)
      ctx.addIssue({
        code: 'custom',
        path: ['body'],
        message: 'Write an internal note of at least three characters.',
      });
  });
export type SupportOperationInput = z.infer<typeof supportOperationInput>;
